/* FizzyIQ — one disclosure language (fizzy-disclosure).
   Animated open/close for every native <details> accordion in the theme.
   Styles: assets/fizzy-disclosure.css

   Why JS is needed at all: a browser hides <details> content the instant the
   `open` attribute goes away, so CSS alone can animate opening but never
   closing. This defers the attribute removal until the collapse transition has
   run. Everything else stays native — <summary> keeps its button semantics,
   keyboard behaviour and find-in-page expansion.

   Under prefers-reduced-motion this does nothing at all: the click handler
   bails, the CSS reverts to display:block, and <details> behaves natively. */
(function () {
  'use strict';

  var mq = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)');
  function reduced() { return !!(mq && mq.matches); }

  /* A <details> qualifies when it is <summary> + exactly one body element.
     Anything else keeps native behaviour rather than being force-fit into the
     two-row grid — the collapse depends on that shape. */
  function bodyOf(d) {
    var body = null;
    for (var i = 0; i < d.children.length; i++) {
      var c = d.children[i];
      if (c.tagName === 'SUMMARY') continue;
      if (body) return null;   /* more than one body element → opt out */
      body = c;
    }
    return body;
  }

  function open(d) {
    if (!d.hasAttribute('data-fzd') || reduced()) { d.open = true; return; }
    if (d.__fzdT) { clearTimeout(d.__fzdT); d.__fzdT = 0; }
    d.open = true;
    /* Commit the 0fr start state before flipping to 1fr, or the browser
       coalesces both into one paint and nothing animates. */
    requestAnimationFrame(function () {
      requestAnimationFrame(function () { d.classList.add('is-open'); });
    });
  }

  function close(d) {
    if (!d.hasAttribute('data-fzd') || reduced()) { d.open = false; return; }
    if (!d.open) return;
    d.classList.remove('is-open');
    if (d.__fzdT) clearTimeout(d.__fzdT);
    /* transitionend is the primary signal; the timeout is the safety net for
       the cases where it never fires (element hidden mid-transition, a tab
       backgrounded, a zero-height body). Without it a row could be left
       permanently open-but-collapsed. */
    var finish = function (e) {
      if (e && e.target !== d) return;                  /* ignore child bubbles; ::details-content reports the <details> itself */
      /* Two branches animate the collapse: grid-template-rows on engines
         without ::details-content, block-size on the pseudo-element where it
         exists. Accept either, or close() would always fall through to the
         600ms safety timeout instead of the 240ms transition. */
      if (e && e.propertyName !== 'grid-template-rows' && e.propertyName !== 'block-size' && e.propertyName !== 'height') return;
      d.removeEventListener('transitionend', finish);
      clearTimeout(d.__fzdT); d.__fzdT = 0;
      d.open = false;
    };
    d.addEventListener('transitionend', finish);
    d.__fzdT = setTimeout(function () { finish(); }, 600);
  }

  function toggle(d) { if (d.open && d.classList.contains('is-open')) close(d); else open(d); }

  function enhance(d) {
    if (d.hasAttribute('data-fzd') || d.hasAttribute('data-fzd-skip')) return;
    if (!bodyOf(d)) return;
    var summary = d.querySelector(':scope > summary');
    if (!summary) return;

    d.setAttribute('data-fzd', '');
    if (d.open) d.classList.add('is-open');

    summary.addEventListener('click', function (e) {
      if (reduced()) return;             /* let the browser do it natively */
      e.preventDefault();
      toggle(d);
    });
  }

  function scan(root) {
    (root || document).querySelectorAll('details').forEach(enhance);
  }

  function init() {
    scan(document);
    /* Sections can render late (Shopify section rendering API, the FR widgets,
       the theme editor). Pick those up without re-scanning on a timer. */
    if (window.MutationObserver) {
      new MutationObserver(function (muts) {
        for (var i = 0; i < muts.length; i++) {
          var added = muts[i].addedNodes;
          for (var j = 0; j < added.length; j++) {
            var n = added[j];
            if (n.nodeType !== 1) continue;
            if (n.tagName === 'DETAILS') enhance(n);
            else if (n.querySelectorAll) scan(n);
          }
        }
      }).observe(document.body, { childList: true, subtree: true });
    }
  }

  /* Published so the existing one-row-at-a-time handlers can close their
     siblings THROUGH the animation instead of yanking the open attribute. */
  window.fizzyDisclosure = { open: open, close: close, toggle: toggle, scan: scan };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
